import {
  CargoSubtype,
  ContainerLevelHistoryEntity,
  getNumberSorter,
  LevelType,
  TransportEntity
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useEffect, useState} from "react";
import {ChartData, ChartDataset, ChartOptions, TooltipItem, ScatterDataPoint} from "chart.js";
import {TFunction} from "i18next";
import {getCargoSubtypeColor} from "../../../util/cargo-subtype-ui-util";
import {Chart, ChartProps} from "../Chart/Chart";

export type ScadaChartProps = Omit<ChartProps<'line'>, 'type' | 'options' | 'data'> & {
  levels?: ContainerLevelHistoryEntity[],
  transports?: TransportEntity[],
  max?: number
};

export const ScadaChart = (props: ScadaChartProps) => {
  const {levels = [], transports = [], max, ...rest} = props;

  const {t,} = useTranslation(["common"])

  const [chartOptions, setChartOptions] = useState<ChartOptions<'line'> | undefined>(undefined);

  const [chartData, setChartData] = useState<ChartData<'line' | 'bar'> | undefined>(undefined);

  useEffect(() => {
    setChartOptions(getChartOptions(t, max))
  }, [t, max])

  useEffect(() => {
    setChartData(getChartData(levels, transports, t))
  }, [levels, t, transports])

  return (
    <Chart<'line' | 'bar'>
      {...rest}
      type="line"
      options={chartOptions}
      data={chartData}/>
  )
};


const getChartOptions = (t: TFunction<"common"[], any, "common"[]>, max: number | undefined): ChartOptions<'line'> => {
  return {
    maintainAspectRatio: true,
    animation: false,
    scales: {
      x: {
        title: {
          display: true,
          text: t<string>("commons.entity.containerlevelhistory.chart.xaxis.label", "Time"),
        },
        type: 'time',
        time: {
          unit: 'hour',
        },
      },
      y: {
        title: {
          display: true,
          text: t<string>("commons.entity.containerlevelhistory.chart.yaxis.label", "Level"),
        },
        beginAtZero: true,
        suggestedMax: max
      },
    },
    plugins: {
      datalabels: {
        display: false,
      },
      tooltip: {
        callbacks: {
          footer: footer
        }
      }
    },
  };
};

const footer = (tooltipItems: TooltipItem<'line'>[]) : string | undefined => {
  let value : string | undefined = undefined;

  if (tooltipItems) {
    for (const item of tooltipItems) {
      // @ts-ignore
      const transport = item.raw?.transport as TransportEntity
      if (transport) {
        if (transport.resourceId) {
          value = "\n"
          value += transport.resourceId
        }
        if (transport.trailerCompanyName) {
          value = "\n"
          value += transport.trailerCompanyName
        }
        if (transport.personName) {
          value += "\n"
          value += transport.personName
        }
        value += `\nDriver Reported: ${transport.quantityDecimal}`
        value += `\nMeter (Standard) Reported: `
        if (transport.scadaQty) {
          value += `${transport.scadaQty}`
          if (transport.scadaTopQty && transport.scadaBottomQty) {
            value += ` (${transport.scadaTopQty} / ${transport.scadaBottomQty})`
          }
        } else {
          value += `Undefined`
        }

        value += `\nMeter (Loess) Reported: `
        if (transport.scada2Qty) {
          value += `${transport.scada2Qty}`
          if (transport.scada2TopQty && transport.scada2BottomQty) {
            value += ` (${transport.scada2TopQty} / ${transport.scada2BottomQty})`
          }
        } else {
          value += `Undefined`
        }

        if (transport.manualCalcQty) {
          value += `\nManual Reported: ${transport.manualCalcQty}`
          if (transport.manualCalcTopQty && transport.manualCalcBottomQty) {
            value += ` ${transport.manualCalcTopQty} / ${transport.manualCalcBottomQty}`
          }
        }

        value += `\nFill Rate Reported: `
        if (transport.fillRate) {
          value += `${transport.fillRate}`
        } else {
          value += `Undefined`
        }
      }
    }
  }

  return value
}

const getChartData = (levels: ContainerLevelHistoryEntity[], transports: TransportEntity[] | undefined, t: TFunction<"common"[], any, "common"[]>,): ChartData<'line' | 'bar'> => {
  const chartDataSets: ChartDataset<'line' | 'bar'>[] = [];

  const groupedLevels = groupLevelsByCargoSubtype(levels)

  groupedLevels.forEach((groupedLevels, cargoSubtype) => {
    chartDataSets.push({
      type: 'line',
      label: t<string>(cargoSubtype.displayValue.id, cargoSubtype.displayValue.defaultMessage),
      fill: false,
      data: getLineChartPoints(groupedLevels, "volume"),
      borderColor: getCargoSubtypeColor(cargoSubtype),
      borderWidth: 3,
      order: 1,
    });
  })

  if (transports && transports.length > 0) {
    chartDataSets.push({
      type: 'bar',
      label: 'Tickets',
      data: transports.map((t) => ({
        x: t.createDttm!,
        y: t.quantityDecimal,
        transport: t,
      })),
      barPercentage: 0.1,
      borderColor: "red",
      borderWidth: 2,
      order: 0,
    })
  }

  return {
    datasets: chartDataSets,
  };
};

const getLineChartPoints = (levels: ContainerLevelHistoryEntity[] | undefined, levelType: LevelType): ScatterDataPoint[] => {
  if (!levels)
    return []

  return levels
    .filter((l) => typeof l.getLevel(levelType) !== 'undefined')
    .sort(getNumberSorter('createDttm'))
    .map((l) => {
      return {
        x: l.createDttm!,
        y: l.getLevel(levelType)!,
      };
    });
};

const groupLevelsByCargoSubtype = (levels: ContainerLevelHistoryEntity[]): Map<CargoSubtype, ContainerLevelHistoryEntity[]> => {
  const groupedLevels = new Map<CargoSubtype, ContainerLevelHistoryEntity[]>();

  levels.forEach(lvl => {
    let group = groupedLevels.get(lvl.cargoSubtype)
    if (!group) {
      group = []
      groupedLevels.set(lvl.cargoSubtype, group)
    }

    group.push(lvl)
  })

  return groupedLevels;
}
